import { useMemo } from 'react'

import { matchFilterNumber, matchFilterString, sortArrayBy } from '@domain-group/javascript-utils'

import { useLoadMore, useWatchEntityUpdates } from '@domain-group/react-hooks'

import useReduxAction from '@hooks/useReduxAction'
import useSelector from '@hooks/useSelector'

import type { AssetLibraryModel, AssetLibraryRequestOptions } from '@models/types'
import type { ModuleState } from '@redux/modules/types'

const watchEntityKeys = ['assetLibraries']

type AssetLibraryFilters = {
  string?: string,
  owner_type?: string,
  owner_id?: number,
}

type UseAssetLibrariesOptions = {
  filters?: AssetLibraryFilters,
  performHttpRequests?: boolean,
  requestOptions?: AssetLibraryRequestOptions,
}

function useAssetLibraries(options: UseAssetLibrariesOptions) {
  const { filters = {}, requestOptions } = options
  const {
    string: filterString,
    owner_type: filterOwnerType,
    owner_id: filterOwnerId,
  } = filters

  const {
    updatedEntities: { assetLibraries: assetLibrariesUpdatedAt },
  } = useWatchEntityUpdates(watchEntityKeys)

  const { assetLibraries } = useSelector(reduxState => reduxState.entities)

  const filteredAssetLibraries = useMemo(() => {
    const filtered = Object.values(assetLibraries).filter((assetLibrary: AssetLibraryModel) => {
      const {
        id, owner_id, owner_type, title,
      } = assetLibrary

      const matchId = id.toString() === filterString
      const matchTitle = matchFilterString(title, filterString)
      const matchString = matchId || matchTitle

      const matchOwnerId = matchFilterNumber(owner_id, filterOwnerId)
      const matchOwnerType = filterOwnerType ? owner_type === filterOwnerType : true

      return matchString && matchOwnerId && matchOwnerType
    })

    return sortArrayBy(filtered, 'asc', 'title')
  }, [assetLibrariesUpdatedAt, JSON.stringify(filters)])

  const filteredAssetLibrariesCount = filteredAssetLibraries.length
  const hasFilteredAssetLibraries = !!filteredAssetLibrariesCount

  const loadMorePayload = useLoadMore({
    filters,
    loadedCount: filteredAssetLibrariesCount,
    performHttpRequests: options.performHttpRequests,
  })

  const {
    callbacks: { loadMore },
    canLoadMore,
    filtersWithOffset,
    limit,
    performHttpRequests,
  } = loadMorePayload

  const { loaded, loading: loadingAssetLibraries } = useReduxAction(
    'assetLibraries',
    'loadAssetLibraries',
    {
      ...requestOptions,
      ...filtersWithOffset,
      limit,
    },
    [filtersWithOffset, performHttpRequests],
    {
      shouldPerformFn: ({ loading }: ModuleState) => performHttpRequests && !loading,
    },
  )

  return {
    callbacks: {
      loadMore,
    },
    canLoadMore,
    filteredAssetLibraries,
    filteredAssetLibrariesCount,
    hasFilteredAssetLibraries,
    loaded,
    loading: loadingAssetLibraries,
  }
}

export default useAssetLibraries
